import { Link, createFileRoute } from '@tanstack/react-router'
import type { ReactNode } from 'react'

import brandCoin from '@/assets/brand/brand-coin.png'
import scallopDivider from '@/assets/decor/scallop-divider.svg'
import bookPlain from '@/assets/features/book-plain.png'
import nepseTape from '@/assets/features/nepse-tape.png'
import wordExplained from '@/assets/features/word-explained.png'
import mitraNamaste from '@/assets/mascot/mitra-namaste.png'
import Reveal from '@/components/motion/Reveal'
import Stagger from '@/components/motion/Stagger'
import { buttonVariants } from '@/components/ui/button-variants'
import SegmentedControl from '@/components/ui/SegmentedControl'
import Tooltip from '@/components/ui/Tooltip'
import { NOT_AVAILABLE_TITLE } from '@/components/ui/empty-state-variants'
import { cn, cssUrl } from '@/lib/utils'
import FeatureCard from '@/screens/onboarding/components/FeatureCard'
import LandingPlans, { LANDING_LINE } from '@/screens/onboarding/components/LandingPlans'

/**
 * THE switch for the EN / नेपाली language pill (mock `lx-lang`). The app has no Nepali copy to switch
 * to and no public language preference (`/auth/preferences/` is for a signed-in member), so this is
 * `false`: the pill is drawn, held on English and disabled, and says "Not available yet" on hover and
 * focus. Flip it to `true` once a Nepali path ships and wire `onChange`.
 */
const LANGUAGE_SWITCH_AVAILABLE: boolean = false

const LANGUAGES = [
  { value: 'en', label: 'EN' },
  { value: 'ne', label: 'नेपाली', lang: 'ne' },
] as const

/** A landing nav word: muted, to the ink on hover and focus (mock `lx-nav`, .16s). */
const NAV_LINK =
  'text-muted-foreground hover:text-foreground focus-visible:text-foreground cursor-pointer text-body-m transition-colors duration-med focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ring-control'

/** Scrolls to a section of this page; no motion when the member asked for none. */
function NavScroll({
  target,
  className,
  children,
}: {
  target: string
  className?: string
  children: ReactNode
}) {
  return (
    <button
      type="button"
      className={cn(NAV_LINK, className)}
      onClick={() => {
        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
        document
          .getElementById(target)
          ?.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' })
      }}
    >
      {children}
    </button>
  )
}

const LEVELS = [
  {
    name: 'Beginners and learners',
    rows: [
      'Today in one sentence',
      'Your first IPO, step by step',
      'Short lessons',
      'Practice with pretend money',
    ],
  },
  {
    name: 'Intermediate and advanced',
    rows: [
      'Live movers and floorsheet',
      'Portfolio, dividends and book closures',
      'Screeners and broker analysis',
      'The full technical chart',
    ],
  },
] as const

const FEATURES = [
  {
    icon: nepseTape,
    title: 'Live NEPSE tape',
    body: 'Movers, depth and the floorsheet, as they print.',
  },
  {
    icon: bookPlain,
    title: 'Your book, read plainly',
    body: 'What you hold, what it earns, what changed.',
  },
  {
    icon: wordExplained,
    title: 'Every word explained',
    body: 'Kitta, bonus, book close, the way a friend would.',
  },
] as const

export const Route = createFileRoute('/welcome')({ component: WelcomeScreen })

/**
 * Scattered marks over the hero panel — the same device `AuthHeroPanel` uses
 * for the sign-in/sign-up screens (see src/screens/auth/components), reused
 * here as plain positioned text rather than a shared component: the two hero
 * panels differ in every other way (box height, mascot, scallop height), and
 * a shared component would need its own generic layer for one array literal.
 *
 * Percent positions carried over from AuthHeroPanel's narrower box.
 * Render-measured against the V2 "Onboarding" frame (no shell) but not
 * re-verified live — flag for phase 2.
 */
const GLYPHS = [
  { id: 'pct', mark: '%', left: '43%', top: '9%', size: 'text-h-l' },
  { id: 'arrow', mark: '↗', left: '13%', top: '14%', size: 'text-body-m' },
  { id: 'diamond', mark: '◇', left: '78%', top: '13%', size: 'text-h-m' },
  { id: 'ring', mark: '○', left: '55%', top: '19%', size: 'text-body-xs' },
  { id: 'tick', mark: '+', left: '84%', top: '28%', size: 'text-body-s' },
] as const

/** Footer links carry no hover of their own (mock: none measured); only the focus ring. */
const FOOTER_LINK =
  'text-body-m focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ring-control'

function FooterColumn({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div>
      <p className="text-muted-foreground-subtle text-h-xs tracking-wide uppercase">{title}</p>
      <ul className="mt-4 space-y-3">{children}</ul>
    </div>
  )
}

function WelcomeScreen() {
  return (
    <Reveal variant="fade-in" className="bg-background text-foreground min-h-dvh">
      <header className="mx-auto flex max-w-360 items-center justify-between px-shell-x py-6 lg:px-30 lg:py-8">
        <div className="flex items-center gap-3">
          <img src={brandCoin} alt="" className="size-8" />
          {/* Hidden under 360px: at 320 the wordmark plus "Sign in" and the
              pill do not fit one row, and "Sign in" wraps onto the logo. The
              logo alone still names the page. */}
          <span className="hidden text-h-l min-[360px]:inline">MoneyMitra</span>
        </div>
        {/* rest:ub (COMPARE-au §5, owner U6) — section links that scroll, Log in, and Start free. Each
            link rests muted and goes to the ink over the fast step; Start free is the outlined small
            button, filling with the raised grey and taking the quieter edge. The EN / नेपाली pill is built behind
            `LANGUAGE_SWITCH_AVAILABLE`; "Get the app" is not (no store listing to link). */}
        <nav aria-label="Sections" className="flex items-center gap-4 sm:gap-8">
          <NavScroll target="features" className="hidden sm:inline">
            Features
          </NavScroll>
          <NavScroll target="plans" className="hidden sm:inline">
            Plans
          </NavScroll>
          <Tooltip title={NOT_AVAILABLE_TITLE} align="center" side="below">
            <SegmentedControl
              options={LANGUAGES}
              value="en"
              onChange={() => undefined}
              ariaLabel="Language"
              size="sm"
              skin="solid"
              disabled={!LANGUAGE_SWITCH_AVAILABLE}
              className="hidden sm:inline-grid"
            />
          </Tooltip>
          <Link to="/signin" className={NAV_LINK}>
            Log in
          </Link>
          <Link
            to="/signup"
            className={cn(buttonVariants({ variant: 'secondary', size: 'md' }), LANDING_LINE)}
          >
            Start free
          </Link>
        </nav>
      </header>

      <main className="mx-auto max-w-360 px-shell-x lg:px-30">
        {/* The 600px lead column is written as `--spacing(150)` (150 × 4px)
            rather than a literal `600px` so the value stays off the
            arbitrary-px ratchet. */}
        <div className="grid items-start gap-16 lg:grid-cols-[minmax(0,--spacing(150))_minmax(0,1fr)]">
          <Stagger as="section" className="pt-12">
            <p className="text-body-xs text-muted-foreground-subtle">Welcome to MoneyMitra</p>
            <h1 className="mt-5 text-display-xl lg:text-display-2xl">
              The share market,
              <br />
              made <span className="text-accent">simple</span>.
            </h1>
            <p className="text-muted-foreground mt-8 max-w-130 text-body-l">
              NEPSE, your book and every hard word, explained the way a friend would. Never a stock
              pick.
            </p>

            <div className="mt-10 flex flex-wrap gap-4">
              {/* Below `sm` the two CTAs stack full-width rather than each
                  hugging its own label, so a short "Get started" and a long
                  "I'll look around first" read as one pair, not two
                  different-sized buttons. */}
              <Link
                to="/signup"
                className={cn(buttonVariants({ size: 'hero' }), 'w-full sm:w-auto')}
              >
                Get started
              </Link>
              <Link
                to="/"
                className={cn(
                  buttonVariants({ variant: 'secondary', size: 'hero' }),
                  'w-full sm:w-auto',
                )}
              >
                I’ll look around first
              </Link>
            </div>

            <p className="text-muted-foreground mt-9 text-body-xs">
              Free to start. No card needed.
            </p>
          </Stagger>

          {/* The hero panel: green ground, mascot, and the scallop tearing the
              bottom edge away into the page background. */}
          <Stagger.Item
            as="section"
            index={2}
            className="bg-hero relative mt-6 hidden h-118 overflow-hidden rounded-lg lg:block"
          >
            {GLYPHS.map((g) => (
              <span
                key={g.id}
                aria-hidden
                className={`text-hero-foreground pointer-events-none absolute opacity-30 ${g.size}`}
                style={{ left: g.left, top: g.top }}
              >
                {g.mark}
              </span>
            ))}

            {/* `bottom-20` (80px), not the `bottom-8` (32px) V2 measured: the
                scallop below is `h-19` (76px) of `bg-background` painted OVER
                this image (later in the DOM, same stacking context), so
                anything under that height had its feet torn away with the
                green. 80 clears it by 4px on the 4-grid. */}
            <img
              src={mitraNamaste}
              alt="Mitra greeting you with a namaste"
              className="absolute bottom-20 left-1/2 h-75 w-auto -translate-x-1/2"
            />
            <div
              className="bg-background absolute inset-x-0 bottom-0 h-19"
              style={{
                maskImage: cssUrl(scallopDivider),
                maskSize: '100% 100%',
                WebkitMaskImage: cssUrl(scallopDivider),
                WebkitMaskSize: '100% 100%',
              }}
            />
          </Stagger.Item>
        </div>

        <Stagger as="ul" id="features" className="mt-16 grid scroll-mt-8 gap-6 md:grid-cols-3">
          {FEATURES.map((f) => (
            <FeatureCard key={f.title} icon={f.icon} title={f.title} body={f.body} />
          ))}
        </Stagger>

        {/* Levels — the mock's two columns, its own words (no figures). */}
        <section aria-labelledby="levels-title" className="mt-24">
          <h2 id="levels-title" className="text-center text-display-l">
            One app. Start at your level.
          </h2>
          <div className="mt-10 grid gap-6 md:grid-cols-2">
            {LEVELS.map((level) => (
              <div key={level.name} className="bg-card border-border-subtle border p-6">
                <h3 className="text-h-2xl">{level.name}</h3>
                <ul className="mt-4 divide-y divide-border-subtle">
                  {level.rows.map((row) => (
                    <li key={row} className="py-3 text-body-l">
                      {row}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        <section id="plans" aria-labelledby="plans-title" className="mt-24 scroll-mt-8">
          <h2 id="plans-title" className="text-center text-display-l">
            Start free. Upgrade when you want more.
          </h2>
          <LandingPlans />
        </section>
      </main>

      {/* Footer — brand, the links this app really has, and the page's own way back up. The mock's
          newsletter box, Learn and Company columns need a public subscribe endpoint and pages that
          do not exist yet, so they are not drawn. */}
      <footer className="border-border-subtle mx-auto mt-24 max-w-360 border-t px-shell-x py-12 lg:px-30">
        <div className="flex flex-wrap items-start justify-between gap-10">
          <div className="flex items-center gap-3">
            <img src={brandCoin} alt="" className="size-8" />
            <span className="text-h-l">MoneyMitra</span>
          </div>
          <nav aria-label="Footer" className="flex flex-wrap gap-x-16 gap-y-8">
            <FooterColumn title="Product">
              <li>
                <NavScroll target="features">Features</NavScroll>
              </li>
              <li>
                <NavScroll target="plans">Plans</NavScroll>
              </li>
            </FooterColumn>
            <FooterColumn title="Account">
              <li>
                <Link to="/signin" className={FOOTER_LINK}>
                  Log in
                </Link>
              </li>
              <li>
                <Link to="/signup" className={FOOTER_LINK}>
                  Create an account
                </Link>
              </li>
            </FooterColumn>
            <FooterColumn title="Legal">
              <li>
                <a href="/terms" className={FOOTER_LINK}>
                  Terms
                </a>
              </li>
              <li>
                <a href="/privacy" className={FOOTER_LINK}>
                  Privacy
                </a>
              </li>
            </FooterColumn>
          </nav>
        </div>
        <div className="text-muted-foreground-subtle mt-12 flex flex-wrap items-center justify-between gap-4 text-body-xs">
          <p>© MoneyMitra. Information, never advice.</p>
          <button
            type="button"
            className={cn(NAV_LINK, 'text-body-xs')}
            onClick={() => {
              const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
              window.scrollTo({ top: 0, behavior: reduced ? 'auto' : 'smooth' })
            }}
          >
            Back to top
          </button>
        </div>
      </footer>
    </Reveal>
  )
}
